import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../AuthContext";
import "./commentpopup.css";

const CommentPopup = ({ onClose, chapterId, lessonId, questionIndex, onNewComment }) => {
  const auth = useAuth();
  const currentUser = auth?.user;
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [activeTab, setActiveTab] = useState("popular");
  const [isHiding, setIsHiding] = useState(false);
  const inputRef = useRef(null);
  const replyInputRef = useRef(null);
  const replyComposerRef = useRef(null);
  const [replyTarget, setReplyTarget] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [expandedReplies, setExpandedReplies] = useState({});
  const [voteOverrides, setVoteOverrides] = useState({});

  const apiUrl = process.env.REACT_APP_API_URL;

  const handleClose = () => {
    setIsHiding(true);
    setTimeout(() => onClose(), 280);
  };

  // 🔹 1. ÎNCARCĂ COMENTARIILE DIN BACKEND
  useEffect(() => {
    fetch(`${apiUrl}/comments/${chapterId}/${lessonId}/${questionIndex}`, {
      credentials: "include", // trimite cookie JWT
    })
      .then((res) => {
        if (res.status === 401) return []; // dacă userul nu e logat
        return res.json();
      })
      .then((data) => setComments(data))
      .catch((err) => console.error("Eroare incarcare comentarii:", err));
  }, [chapterId, lessonId, questionIndex, apiUrl]);

const submitComment = async (textToSend, parentId = null) => {
  if (!textToSend.trim()) return;

  const body = {
    chapter_id: chapterId,
    lesson_id: lessonId,
    question_index: questionIndex,
    comment_text: textToSend,
    parent_id: parentId   // 🔥 nou
  };

  const res = await fetch(`${apiUrl}/comments`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (res.status === 401) {
    alert("Trebuie sa fii autentificat pentru a posta comentarii.");
    return;
  }

  const saved = await res.json();

  setComments((prev) => [...prev, saved]);

  if (onNewComment) onNewComment();

  return saved;
};

  // 🔹 2. TRIMITE UN COMMENT LA BACKEND
  const handlePost = async () => {
    const posted = await submitComment(newComment);
    if (!posted) return;
    setNewComment("");
  };

  const getAvatarInitial = (username) => {
    if (!username) return "U";
    return username.trim().charAt(0).toUpperCase();
  };

  const getAvatarUrl = (profilePicture) => {
    if (!profilePicture) return "";

    const trimmedPicture = profilePicture.trim();
    if (!trimmedPicture) return "";

    if (
      trimmedPicture.startsWith("http://") ||
      trimmedPicture.startsWith("https://") ||
      trimmedPicture.startsWith("data:")
    ) {
      return trimmedPicture;
    }

    if (trimmedPicture.startsWith("/") && apiUrl) {
      return `${apiUrl}${trimmedPicture}`;
    }

    return trimmedPicture;
  };

  const getCommentAvatarUrl = (comment) => {
    const commentAvatar = getAvatarUrl(comment?.profile_picture);
    if (commentAvatar) return commentAvatar;

    const isCurrentUserComment =
      currentUser &&
      (
        (comment?.user_id && currentUser?.user_id && Number(comment.user_id) === Number(currentUser.user_id)) ||
        (comment?.username && currentUser?.username && comment.username === currentUser.username)
      );

    if (isCurrentUserComment) {
      return getAvatarUrl(currentUser?.profile_picture);
    }

    return "";
  };

  const getPopularityScore = (comment) => {
    return Number(
      comment.vote_count ??
      comment.likes_count ??
      comment.upvotes ??
      comment.votes ??
      comment.replies_count ??
      0
    );
  };

  const isBackendVoted = (comment) => Boolean(comment?.user_has_upvoted);

  const isCommentVoted = (comment) => {
    if (Object.prototype.hasOwnProperty.call(voteOverrides, comment.id)) {
      return voteOverrides[comment.id];
    }
    return isBackendVoted(comment);
  };

  const getCommentVoteCount = (comment) => {
    const baseCount = getPopularityScore(comment);
    const backendVoted = isBackendVoted(comment);
    const currentVoted = isCommentVoted(comment);

    const adjustedCount = baseCount + (currentVoted ? 1 : 0) - (backendVoted ? 1 : 0);
    return Math.max(0, adjustedCount);
  };

const handleVoteToggle = async (comment) => {
  if (!currentUser) {
    alert("Trebuie sa fii autentificat pentru a vota.");
    return;
  }

  const currentlyVoted = isCommentVoted(comment);
  const newVoteType = currentlyVoted ? 0 : 1; 
  // 0 = remove vote, 1 = upvote

  try {
    const res = await fetch(`${apiUrl}/comments/${comment.id}/vote`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ vote_type: newVoteType }),
    });

    if (!res.ok) throw new Error("Vote error");

    const data = await res.json();

    // actualizezi scorul real din backend
    setComments((prev) =>
      prev.map((c) =>
        c.id === comment.id
          ? {
              ...c,
              vote_count: data.score,
              user_has_upvoted: data.user_has_upvoted,
            }
          : c
      )
    );

  } catch (err) {
    console.error("Vote error:", err);
  }
};

  const getReplyMentionsScore = (comment, allComments) => {
    const username = (comment?.username || "").trim();
    if (!username) return 0;

    const mentionPrefix = `@${username.toLowerCase()}`;
    return allComments.reduce((count, currentComment) => {
      const text = (currentComment?.comment_text || "").trim().toLowerCase();
      return text.startsWith(mentionPrefix) ? count + 1 : count;
    }, 0);
  };

  const formatRelativeTime = (createdAt) => {
    if (!createdAt) return "";

    const createdDate = new Date(createdAt);
    if (Number.isNaN(createdDate.getTime())) return "";

    const diffMs = Date.now() - createdDate.getTime();
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;
    const week = 7 * day;
    const month = 30 * day;
    const year = 365 * day;

    if (diffMs < minute) return "acum";
    if (diffMs < hour) {
      const value = Math.floor(diffMs / minute);
      return `${value} minut${value > 1 ? "e" : ""}`;
    }
    if (diffMs < day) {
      const value = Math.floor(diffMs / hour);
      return `${value} or${value > 1 ? "e" : "a"}`;
    }
    if (diffMs < week) {
      const value = Math.floor(diffMs / day);
      return `${value} zi${value > 1 ? "le" : ""}`;
    }
    if (diffMs < month) {
      const value = Math.floor(diffMs / week);
      return `${value} saptaman${value > 1 ? "i" : "a"}`;
    }
    if (diffMs < year) {
      const value = Math.floor(diffMs / month);
      return `${value} lun${value > 1 ? "i" : "a"}`;
    }
    const value = Math.floor(diffMs / year);
    return `${value} ${value > 1 ? "ani" : "an"}`;
  };

  const handleReply = (comment) => {
    setReplyTarget(comment);
    setReplyText("");
    setTimeout(() => replyInputRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (!replyTarget) return;

    const handleOutsideReplyClick = (event) => {
      const clickedInsideComposer = replyComposerRef.current?.contains(event.target);
      if (clickedInsideComposer) return;

      setReplyTarget(null);
      setReplyText("");
    };

    document.addEventListener("mousedown", handleOutsideReplyClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideReplyClick);
    };
  }, [replyTarget]);

const handleSendReply = async () => {
  if (!replyTarget || !replyText.trim()) return;

  const posted = await submitComment(
    replyText.trim(),
    replyTarget.id   // 🔥 aici trimitem parent_id
  );

  if (!posted) return;

  setExpandedReplies((prev) => ({
    ...prev,
    [replyTarget.id]: true,
  }));

  setReplyText("");
  setReplyTarget(null);
};

  const sortedComments = [...comments].sort((first, second) => {
    const firstDate = new Date(first.created_at).getTime() || 0;
    const secondDate = new Date(second.created_at).getTime() || 0;

    if (activeTab === "recent") {
      return secondDate - firstDate;
    }

    const firstPopularity = getPopularityScore(first) + getReplyMentionsScore(first, comments);
    const secondPopularity = getPopularityScore(second) + getReplyMentionsScore(second, comments);

    if (secondPopularity !== firstPopularity) {
      return secondPopularity - firstPopularity;
    }

    return firstDate - secondDate;
  });


  const rootComments = sortedComments.filter(c => !c.parent_id);
const replies = sortedComments.filter(c => c.parent_id);


  return (
    <div className={`comment-popup-overlay${isHiding ? ' hiding' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className={`comment-popup${isHiding ? ' hiding' : ''}`}>

        {/* Buton închidere */}
       

        <div className="comment-top-row">
          <button className="comment-hide-btn" type="button" onClick={handleClose}>
            Ascunde <span className="comment-hide-arrow">›</span>
          </button>
          <span className="comment-total">{comments.length} comentarii</span>
        </div>

        <div className="comment-title-row">
          <h2 className="comment-header">Comentarii</h2>
        </div>

        {/* Input + Post */}
        <div className="comment-input-row">
          <textarea
            placeholder="Scrie un comentariu..."
            className="comment-input"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            rows={2}
            ref={inputRef}
          />
          <button className="comment-post-btn" onClick={handlePost}>
            Post
          </button>
        </div>

        <div className="comment-tabs" role="tablist" aria-label="Sortare comentarii">
          <button
            className={`comment-tab ${activeTab === "popular" ? "active" : ""}`}
            onClick={() => setActiveTab("popular")}
            type="button"
          >
            Popular
          </button>
          <button
            className={`comment-tab ${activeTab === "recent" ? "active" : ""}`}
            onClick={() => setActiveTab("recent")}
            type="button"
          >
            Recent
          </button>
        </div>

        {/* Listă comentarii */}
        <div className="comment-list">
          {comments.length === 0 && (
            <p className="no-comments">Fii primul care comenteaza! ✍️</p>
          )}

          {rootComments.map((c) => {
  const childReplies = replies
    .filter((r) => r.parent_id === c.id)
    .sort((a, b) => {
      const aDate = new Date(a.created_at).getTime() || 0;
      const bDate = new Date(b.created_at).getTime() || 0;
      return aDate - bDate;
    });

  const isRepliesExpanded = !!expandedReplies[c.id];

  return (
    <div key={c.id} className="comment-thread">

      {/* Comentariu principal */}
      <div className="comment-item">
        <div className="comment-avatar">
          {getCommentAvatarUrl(c) ? (
            <img
              src={getCommentAvatarUrl(c)}
              alt={c.username || "Utilizator"}
              className="comment-avatar-image"
            />
          ) : (
            getAvatarInitial(c.username)
          )}
        </div>

        <div className="comment-body">
          <div className="comment-user-row">
            <b className="comment-username">{c.username || "Utilizator"}</b>
            <span className="comment-time">
              {formatRelativeTime(c.created_at)}
            </span>
          </div>

          <p className="comment-text">{c.comment_text}</p>

          <div className="comment-meta-row">
            <button
              className={`comment-vote-btn ${isCommentVoted(c) ? "active" : ""}`}
              type="button"
              onClick={() => handleVoteToggle(c)}
            >
              ↑ {getCommentVoteCount(c)}
            </button>

            <button
              className="comment-reply"
              type="button"
              onClick={() => handleReply(c)}
            >
              Raspunde
            </button>

            {childReplies.length > 0 && (
              <button
                className={`comment-toggle-replies ${isRepliesExpanded ? "expanded" : ""}`}
                type="button"
                onClick={() =>
                  setExpandedReplies((prev) => ({
                    ...prev,
                    [c.id]: !prev[c.id],
                  }))
                }
              >
                <span className="comment-toggle-arrow" aria-hidden="true">▸</span>
                <span className="comment-toggle-label">
                  {childReplies.length} {childReplies.length === 1 ? "raspuns" : "raspunsuri"}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 🔥 Reply-uri */}
      {isRepliesExpanded && childReplies.length > 0 && (
        <div className="reply-thread-group">
          {childReplies.map((r) => (
            <div key={r.id} className="reply-item">
              <div className="comment-item reply">
                <div className="comment-avatar comment-avatar-reply">
                  {getCommentAvatarUrl(r) ? (
                    <img
                      src={getCommentAvatarUrl(r)}
                      alt={r.username || "Utilizator"}
                      className="comment-avatar-image"
                    />
                  ) : (
                    getAvatarInitial(r.username)
                  )}
                </div>

                <div className="comment-body comment-body-reply">
                  <div className="comment-user-row">
                    <b className="comment-username">
                      {r.username || "Utilizator"}
                    </b>
                    <span className="comment-time">
                      {formatRelativeTime(r.created_at)}
                    </span>
                  </div>

                  <p className="comment-text">{r.comment_text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Composer reply */}
      {replyTarget?.id === c.id && (
        <div className="reply-composer-row" ref={replyComposerRef}>
          <div className="reply-avatar">
            {getAvatarUrl(currentUser?.profile_picture) ? (
              <img
                src={getAvatarUrl(currentUser?.profile_picture)}
                alt={currentUser?.username || "Utilizator"}
                className="reply-avatar-image"
              />
            ) : (
              <FontAwesomeIcon icon={faUser} />
            )}
          </div>

          <div className="reply-composer-input-wrap">
            <input
              ref={replyInputRef}
              type="text"
              className="reply-composer-input"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSendReply();
                }
              }}
            />
            <button
              className="reply-post-btn"
              type="button"
              onClick={handleSendReply}
            >
              Post
            </button>
          </div>
        </div>
      )}

    </div>
  );
})}
         
        </div>

      </div>
    </div>
  );
};

export default CommentPopup;
